/**
 * NeedsAttention — the full-page alerts surface behind the topbar bell and the
 * dashboard's "Needs attention" CTAs (route id `notifications`).
 *
 * #804 Part A (2026-07-13 docket §3): this route previously rendered the
 * "Being migrated" placeholder. It now consumes the SAME canonical source as
 * the dashboard's Needs-attention column — getDashboardAlerts via
 * useDashboard() — so the bell, the dashboard column, and this screen always
 * agree. No client-fabricated alerts; empty means genuinely all quiet.
 */
import { Btn, Card, Icon, T, tx } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { useDashboard } from "./useDashboard";
import { useT } from "../../lib/i18n";
import type { DashboardAlert } from "@v0y4ge/api-contracts";

/* Tone → accent color (same mapping the dashboard column uses). */
const TONE_ACCENT: Record<string, string> = { primary: T.p1, blue: T.tDarkBlue, green: T.green1, orange: T.orange1, red: T.red1 };
/* ctaHref → {route, params} — same parsing as Dashboard.goHref. Server hrefs are
 * real app URLs that may carry a query string ("/reports?range=today&
 * from=2026/07/14&metric=events/run") and slashed sub-views; the old bare
 * dash→dot replace mangled query-bearing hrefs into garbage route ids → 404. */
const hrefToEntry = (href?: string): { route: string; params: Record<string, string> } => {
  const [path = "", query = ""] = String(href || "").split("?");
  const route = path.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean).join(".").replace(/-/g, ".");
  const params: Record<string, string> = {};
  try { new URLSearchParams(query).forEach((v, k) => { params[k] = v; }); } catch { /* malformed query — route still resolves */ }
  // Same unmigrated-report fallback as Dashboard.hrefToEntry (operator call,
  // 2026-07-14): these CTAs must never dead-end on the MigrationPlaceholder.
  const UNMIGRATED_REPORT_FALLBACK: Record<string, string> = { "registrations.today": "reports", "checkins.today": "reports" };
  return { route: UNMIGRATED_REPORT_FALLBACK[route] ?? route, params };
};

export default function NeedsAttention() {
  const nav = useNav();
  const t = useT(); // i18n pilot surface 2 (kit v4.2): dashboard.needsAttention.* keys
  const { alerts, loading, error, reload } = useDashboard();
  const items: DashboardAlert[] = alerts?.alerts ?? [];

  return (
    <Screen rail="dashboard" topbar>
      <PageHeader
        title={t("dashboard.needsAttention.title", "Needs attention")}
        subtitle={loading ? t("dashboard.needsAttention.checkingYourWorkspace", "Checking your workspace…") : items.length ? t("dashboard.needsAttention.itemsToReviewCount.icu", "{count, plural, one {# item to review} other {# items to review}}", { count: items.length }) : t("dashboard.needsAttention.nothingNeedsYourAttentionRightNow", "Nothing needs your attention right now")}
        breadcrumb={<span style={{ cursor: "pointer" }} onClick={() => nav.go("dashboard")}>{t("dashboard.needsAttention.text", "← Dashboard")}</span>}
        actions={<Btn variant="secondary" icon={<Icon.refresh size={14} />} onClick={reload}>{t("dashboard.needsAttention.text2", "Refresh")}</Btn>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px" }}>
        <div style={{ maxWidth: 760, display: "flex", flexDirection: "column", gap: 12 }}>
          {error ? (
            <div role="alert" style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, color: T.red1, fontSize: 13 }}>
              <Icon.warn size={14} /> {t("dashboard.needsAttention.text3", "Couldn't load alerts.")} <span style={{ color: T.p1, cursor: "pointer", fontWeight: 600 }} onClick={reload}>{t("dashboard.needsAttention.text4", "Retry")}</span>
            </div>
          ) : null}

          {loading && !items.length ? (
            <Card style={{ padding: 24, fontSize: 13, color: T.soft }}>{t("dashboard.needsAttention.text5", "Loading alerts…")}</Card>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <Card style={{ padding: "48px 24px", textAlign: "center" }}>
              <div style={{ width: 52, height: 52, borderRadius: 12, background: T.n6, color: T.green1, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                <Icon.check size={22} />
              </div>
              <h2 style={{ ...tx.h3, fontWeight: 700, margin: "0 0 6px" }}>{t("dashboard.needsAttention.text6", "All quiet on the events front")}</h2>
              <p style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.55, margin: 0 }}>{t("dashboard.needsAttention.text7", "Alerts about approvals, capacity, comms and live operations will appear here as they come up.")}</p>
            </Card>
          ) : null}

          {items.map((a, i) => {
            const accent = TONE_ACCENT[a.tone as string] || T.p1;
            const entry = hrefToEntry(a.ctaHref);
            const route = entry.route;
            return (
              <Card key={i} style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ display: "flex", gap: 14, padding: 16, alignItems: "flex-start" }}>
                  <span style={{ width: 34, height: 34, borderRadius: 8, background: T.n6, color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon.info size={16} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{a.title}</div>
                    {a.body ? <div style={{ fontSize: 13, color: T.n2, marginTop: 3, lineHeight: 1.5 }}>{a.body}</div> : null}
                  </div>
                  {route ? (
                    <Btn variant="secondary" size="sm" iconRight={<Icon.arrow dir="right" size={12} />} onClick={() => nav.go(route, entry.params)}>
                      {a.ctaLabel || t("common.actions.open", "Open")}
                    </Btn>
                  ) : null}
                </div>
                <div style={{ height: 3, background: accent }} />
              </Card>
            );
          })}
        </div>
      </div>
    </Screen>
  );
}
